import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { getSCGRuleSourceInfo, getRuleUsage } from '../services/api';
import TrustScoreBadge from './TrustScoreBadge';
import ViewInPdfButton from './ViewInPdfButton';

const classificationColors = {
  'TOP SECRET': { bg: 'rgba(234,179,8,0.15)', text: '#eab308', border: 'rgba(234,179,8,0.4)' },
  'SECRET': { bg: 'rgba(239,68,68,0.15)', text: '#ef4444', border: 'rgba(239,68,68,0.4)' },
  'CONFIDENTIAL': { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6', border: 'rgba(59,130,246,0.4)' },
  'UNCLASSIFIED': { bg: 'rgba(34,197,94,0.15)', text: '#22c55e', border: 'rgba(34,197,94,0.4)' },
};

const RuleDetailModal = ({ sourceInfo, ruleId, onClose }) => {
  const rule = sourceInfo?.rule_data || sourceInfo?.rule_details || {};
  const classLevel = rule.classification_level || sourceInfo?.rule_details?.classification_level || 'UNKNOWN';
  const colors = classificationColors[classLevel] || { bg: 'rgba(107,114,128,0.15)', text: '#9ca3af', border: 'rgba(107,114,128,0.4)' };

  const { data: usage } = useQuery({
    queryKey: ['rule-usage', ruleId],
    queryFn: () => getRuleUsage(ruleId),
    enabled: !!ruleId,
    staleTime: 60 * 1000,
    retry: false,
  });

  const formatArray = (val) => {
    if (Array.isArray(val)) return val;
    if (val) return [val];
    return [];
  };

  const compartments = formatArray(rule.compartment_caveats);
  const dissemination = formatArray(rule.dissemination_control);
  const keywords = formatArray(rule.keywords);

  const fullClassification = [
    classLevel,
    compartments.length ? `//${compartments.join('/')}` : '',
    dissemination.length ? `//${dissemination.join('/')}` : ''
  ].join('');

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const copyId = () => {
    navigator.clipboard.writeText(ruleId);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg mx-4 rounded-lg overflow-hidden"
        style={{ background: 'var(--vault-surface, #1a1f2e)', border: '1px solid var(--vault-border, #2d3348)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--vault-border, #2d3348)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheckIcon className="w-5 h-5 flex-shrink-0" style={{ color: colors.text }} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary, #e2e8f0)' }}>
                {sourceInfo?.citation_display?.friendly_reference || 'SCG Rule'}
              </div>
              <div className="text-xs font-mono" style={{ color: 'var(--text-muted, #64748b)' }}>{ruleId}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {usage && usage.cited_count > 0 && (
              <TrustScoreBadge
                trustScore={usage.trust_score}
                citedCount={usage.cited_count}
                overrideCount={usage.override_count}
                size="md"
              />
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-white/10 transition-colors">
              <XMarkIcon className="w-5 h-5" style={{ color: 'var(--text-muted, #64748b)' }} />
            </button>
          </div>
        </div>

        {/* Classification Banner */}
        <div className="px-5 py-3" style={{ background: colors.bg, borderBottom: `1px solid ${colors.border}` }}>
          <div className="text-sm font-bold font-mono tracking-wider" style={{ color: colors.text }}>
            {fullClassification}
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto" style={{ color: 'var(--text-secondary, #94a3b8)' }}>
          {/* Description / Item */}
          {(rule.item || rule.description || sourceInfo?.rule_details?.enhanced_description) && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted, #64748b)' }}>
                Description
              </label>
              <div className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--text-primary, #e2e8f0)' }}>
                {rule.item || rule.description || sourceInfo?.rule_details?.enhanced_description}
              </div>
            </div>
          )}

          {/* Reason */}
          {rule.reason && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted, #64748b)' }}>
                Reason
              </label>
              <div className="mt-1 text-sm" style={{ color: 'var(--text-primary, #e2e8f0)' }}>
                {rule.reason}
              </div>
            </div>
          )}

          {/* Keywords */}
          {keywords.length > 0 && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted, #64748b)' }}>
                Keywords
              </label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {keywords.map((kw, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs rounded" style={{
                    background: 'var(--vault-elevated, #232840)',
                    color: 'var(--text-secondary, #94a3b8)',
                    border: '1px solid var(--vault-border, #2d3348)'
                  }}>
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Compartments & Dissemination */}
          <div className="flex gap-6">
            {compartments.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted, #64748b)' }}>
                  Compartments
                </label>
                <div className="mt-1 text-sm" style={{ color: 'var(--text-primary, #e2e8f0)' }}>
                  {compartments.join(', ')}
                </div>
              </div>
            )}
            {dissemination.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted, #64748b)' }}>
                  Dissemination
                </label>
                <div className="mt-1 text-sm" style={{ color: 'var(--text-primary, #e2e8f0)' }}>
                  {dissemination.join(', ')}
                </div>
              </div>
            )}
          </div>

          {/* Source */}
          {(rule.source || sourceInfo?.citation_display?.page_reference) && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted, #64748b)' }}>
                Source
              </label>
              <div className="mt-1 text-xs" style={{ color: 'var(--text-muted, #64748b)' }}>
                {rule.source || sourceInfo?.citation_display?.page_reference}
              </div>
            </div>
          )}

          {/* Context */}
          {(sourceInfo?.rule_details?.context_agency || sourceInfo?.rule_details?.context_section) && (
            <div className="flex flex-wrap gap-2">
              {sourceInfo.rule_details.context_agency && (
                <span className="px-2 py-0.5 text-xs rounded" style={{
                  background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)'
                }}>
                  {sourceInfo.rule_details.context_agency}
                </span>
              )}
              {sourceInfo.rule_details.context_section && (
                <span className="px-2 py-0.5 text-xs rounded" style={{
                  background: 'var(--vault-elevated, #232840)',
                  color: 'var(--text-secondary, #94a3b8)',
                  border: '1px solid var(--vault-border, #2d3348)'
                }}>
                  {sourceInfo.rule_details.context_section}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: '1px solid var(--vault-border, #2d3348)', background: 'var(--vault-elevated, #232840)' }}>
          <button
            onClick={copyId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors hover:bg-white/5"
            style={{ color: 'var(--text-muted, #64748b)' }}
          >
            <ClipboardDocumentIcon className="w-3.5 h-3.5" />
            Copy ID
          </button>
          <div className="flex gap-2 items-center">
            {sourceInfo?.source_available && (
              <ViewInPdfButton
                ruleId={ruleId}
                sourceInfo={sourceInfo}
                variant="text-link"
                size="md"
                stopPropagation={false}
              />
            )}
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded transition-colors"
              style={{ background: 'var(--vault-surface, #1a1f2e)', color: 'var(--text-secondary, #94a3b8)', border: '1px solid var(--vault-border, #2d3348)' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const SCGCitation = ({ ruleId, compact = false, showClassificationBadge = true }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { data: sourceInfo, isLoading, error } = useQuery({
    queryKey: ['scg-source', ruleId],
    queryFn: () => getSCGRuleSourceInfo(ruleId),
    enabled: !!ruleId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <span className="inline-flex items-center text-sm" style={{ color: 'var(--text-muted, #64748b)' }}>
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 mr-1" style={{ borderColor: 'var(--text-muted, #64748b)' }}></div>
        {ruleId}
      </span>
    );
  }

  if (error || !sourceInfo) {
    return (
      <span className="inline-flex items-center font-mono text-sm" style={{ color: 'var(--error, #ef4444)' }}>
        <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
        {ruleId}
      </span>
    );
  }

  if (compact) {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="relative inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer"
          style={{
            background: 'rgba(59,130,246,0.1)',
            color: '#60a5fa',
            border: '1px solid rgba(59,130,246,0.25)',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(59,130,246,0.2)';
            e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'rgba(59,130,246,0.1)';
            e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
          }}
          title="Click to view rule details"
        >
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
          {ruleId}
          <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1 opacity-50" />

          {showTooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs rounded-lg shadow-lg z-50 whitespace-nowrap pointer-events-none"
              style={{ background: 'var(--vault-elevated, #232840)', color: 'var(--text-primary, #e2e8f0)', border: '1px solid var(--vault-border, #2d3348)' }}>
              {sourceInfo.citation_display?.friendly_reference || sourceInfo.rule_details?.item || ruleId}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent" style={{ borderTopColor: 'var(--vault-elevated, #232840)' }}></div>
            </div>
          )}
        </button>
        {showModal && (
          <RuleDetailModal
            sourceInfo={sourceInfo}
            ruleId={ruleId}
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  // Full (non-compact) display — used when embedded in rule lists
  const classLevel = sourceInfo.rule_details?.classification_level || 'UNKNOWN';
  const colors = classificationColors[classLevel] || { bg: 'rgba(107,114,128,0.15)', text: '#9ca3af', border: 'rgba(107,114,128,0.4)' };

  return (
    <>
      <div className="rounded-lg p-4 space-y-3 cursor-pointer transition-all hover:ring-1"
        style={{
          background: 'var(--vault-surface, #1a1f2e)',
          border: '1px solid var(--vault-border, #2d3348)',
          '--tw-ring-color': 'rgba(59,130,246,0.4)',
        }}
        onClick={() => setShowModal(true)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <ShieldCheckIcon className="w-5 h-5" style={{ color: '#60a5fa' }} />
            <div>
              <h4 className="text-sm font-medium" style={{ color: 'var(--text-primary, #e2e8f0)' }}>
                {sourceInfo.citation_display?.friendly_reference || 'SCG Rule'}
              </h4>
              <p className="text-xs font-mono" style={{ color: 'var(--text-muted, #64748b)' }}>{ruleId}</p>
            </div>
          </div>

          {showClassificationBadge && classLevel !== 'UNKNOWN' && (
            <span className="px-2 py-1 text-xs font-medium rounded" style={{
              background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`
            }}>
              {classLevel}
            </span>
          )}
        </div>

        {sourceInfo.rule_details?.enhanced_description && (
          <p className="text-sm" style={{ color: 'var(--text-secondary, #94a3b8)' }}>
            {sourceInfo.rule_details.enhanced_description}
          </p>
        )}

        <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--vault-border, #2d3348)' }}>
          <div className="text-xs" style={{ color: 'var(--text-muted, #64748b)' }}>
            {sourceInfo.source_available ? (
              <>
                <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                {sourceInfo.citation_display?.page_reference || 'Source Available'}
              </>
            ) : (
              'Click for details'
            )}
          </div>
          <span className="text-xs" style={{ color: '#60a5fa' }}>View Details &rarr;</span>
        </div>
      </div>
      {showModal && (
        <RuleDetailModal
          sourceInfo={sourceInfo}
          ruleId={ruleId}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default SCGCitation;
